/**
 * ClientPortalCustomer — customer-level landing page. One link that
 * lists every project belonging to a customer. Opened via a shareable,
 * expiring token URL:
 *   /klient/c/:token
 *
 * Clicking a project card drills into the same view the per-project
 * portal shows (Detaljer + Underlag + Kontroll + SJA + Progresjon),
 * but authenticated via the customer token — the sub-page URL is:
 *   /klient/c/:token/p/:projectId
 *
 * All UI text is Norwegian.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, AlertCircle, MapPin, User as UserIcon, Building2, ChevronRight,
  ArrowLeft, Clock, CheckCircle2, FileText,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
    return d.toLocaleDateString("no-NO", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

// True when the timestamp is within the last `days` days (used for the "updated" dot).
function isRecent(iso, days = 7) {
  if (!iso) return false;
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
    return Date.now() - d.getTime() <= days * 86400000;
  } catch { return false; }
}

function ErrorPage({ title, hint }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md text-center shadow-md">
        <div className="w-14 h-14 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
          <AlertCircle className="w-7 h-7 text-red-500" />
        </div>
        <h1 className="text-xl font-black text-slate-900 mb-2">{title}</h1>
        {hint && <p className="text-sm text-slate-600">{hint}</p>}
      </div>
    </div>
  );
}

export default function ClientPortalCustomer() {
  // Route is `/klient/c/:customerToken`
  const { customerToken: token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/client-portal/customer/${token}`);
        setData(data);
      } catch (e) {
        if (e.response?.status === 410) setError({ title: "Lenken er utløpt eller tilbakekalt", hint: "Kontakt oss for en ny lenke." });
        else if (e.response?.status === 404) setError({ title: "Ugyldig lenke", hint: "Sjekk URL-en du fikk tilsendt." });
        else setError({ title: "Kunne ikke laste inn", hint: e.response?.data?.detail || "Prøv igjen litt senere." });
      }
      setLoading(false);
    })();
  }, [token]);

  const stats = useMemo(() => {
    if (!data?.projects) return { total: 0, unsigned: 0, awaiting: 0 };
    return {
      total: data.projects.length,
      unsigned: data.projects.reduce((s, p) => s + (p.underlags_unsigned || 0), 0),
      awaiting: data.projects.filter((p) => p.underlags_unsigned > 0).length,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }
  if (error) return <ErrorPage title={error.title} hint={error.hint} />;
  if (!data) return null;

  const { customer, projects, expires_at } = data;

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="customer-portal-root">
      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 pb-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8">
          <div className="text-[10px] uppercase tracking-widest font-black text-cyan-300 mb-1">Kundeportal · Doserne Utleie AS</div>
          <h1 className="text-2xl sm:text-4xl font-black text-white tracking-tight" data-testid="customer-title">
            {customer.name || "Kunde"}
          </h1>
          {customer.email && (
            <div className="text-sm text-slate-300 mt-1">{customer.email}</div>
          )}
          <div className="mt-4 grid grid-cols-3 gap-3 max-w-md">
            <div>
              <div className="text-2xl font-black text-white">{stats.total}</div>
              <div className="text-[10px] uppercase tracking-wider font-black text-slate-400">Prosjekter</div>
            </div>
            <div>
              <div className="text-2xl font-black text-amber-300">{stats.unsigned}</div>
              <div className="text-[10px] uppercase tracking-wider font-black text-slate-400">Ikke signert</div>
            </div>
            <div>
              <div className="text-2xl font-black text-cyan-300">{stats.awaiting}</div>
              <div className="text-[10px] uppercase tracking-wider font-black text-slate-400">Venter</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-6">
        <div className="text-xs uppercase tracking-widest font-black text-slate-500 mb-3">
          Prosjekter ({projects.length})
        </div>
        {projects.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500 italic">
            Ingen prosjekter registrert på denne kunden enda.
          </div>
        ) : (
          <div className="space-y-2">
            {projects.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/klient/c/${token}/p/${p.id}`)}
                className={`w-full text-left bg-white border-2 rounded-2xl p-4 flex items-center gap-3 hover:shadow-md active:scale-[0.99] transition ${p.underlags_unsigned > 0 ? "border-amber-300 hover:border-amber-400" : "border-slate-200 hover:border-cyan-400"}`}
                data-testid={`customer-project-card-${p.id}`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${p.underlags_unsigned > 0 ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-600"}`}>
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    {p.number && <span className="text-[10px] font-mono uppercase text-slate-500">#{p.number}</span>}
                    {isRecent(p.last_updated) && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black text-cyan-700">
                        <span className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" /> Oppdatert
                      </span>
                    )}
                    {p.status && p.status !== "active" && (
                      <span className="text-[9px] uppercase tracking-widest font-black text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-1.5 py-0.5">
                        {p.status}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-black text-slate-900 truncate">{p.name || p.address || "Prosjekt"}</div>
                  {p.address && p.address !== p.name && (
                    <div className="text-xs text-slate-500 truncate inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" /> {p.address}
                    </div>
                  )}
                  <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                    {p.start_date && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-slate-400" /> {fmtDate(p.start_date)}</span>}
                    {p.underlags_total > 0 && (
                      <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3 text-slate-400" /> {p.underlags_total} underlag</span>
                    )}
                    {p.last_updated && (
                      <span className="inline-flex items-center gap-1 text-slate-400">Oppdatert {fmtDate(p.last_updated)}</span>
                    )}
                  </div>
                </div>
                {p.underlags_unsigned > 0 ? (
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5">
                      <Clock className="w-3 h-3" /> {p.underlags_unsigned}
                    </span>
                    <ChevronRight className="w-4 h-4 text-amber-500" />
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="text-[10px] uppercase tracking-widest text-slate-400 text-center pt-8">
          Lenke gyldig til {fmtDate(expires_at)}
        </div>
      </div>
    </div>
  );
}
